/**
 * Alpha Workwear - loading skeletons for images.
 *
 * Paints a shimmering placeholder inside every lazy image's box until the
 * bitmap arrives, so a slow connection shows the SHAPE of the page rather than
 * a field of blank rectangles.
 *
 * Entirely additive. If this file never runs, no image gets `.alpha-skel`, the
 * CSS matches nothing, and the shop behaves exactly as it does today.
 *
 * The three cases that actually matter, in the order they bite:
 *
 *   ALREADY DECODED. This script is deferred, so by the time it runs some
 *   images have already loaded - a cached repeat visit finishes most of them
 *   before any script executes. Marking those as skeletons would shimmer an
 *   image that is sitting right there, so `complete && naturalWidth` is checked
 *   before anything is added, not after.
 *
 *   NEVER ARRIVES. A 404, a blocked CDN, a dead connection: `load` never fires.
 *   `error` is treated exactly like `load`, because a skeleton that shimmers
 *   forever is worse than an empty box - it promises something that is not
 *   coming.
 *
 *   INJECTED LATER. The cart drawer, the recommendations and every facet change
 *   replace whole blocks of markup containing fresh <img> elements that were
 *   never in the document when this ran. Those are picked up by a
 *   MutationObserver, which is the only reason this holds together on the
 *   collection pages.
 */

(function () {
  var SKEL = 'alpha-skel';
  var DONE = 'is-loaded';

  function settle(img) {
    img.classList.add(DONE);
  }

  function track(img) {
    // Only images the browser will defer. The logo and the hero are eager and
    // land with the document; dressing them in a skeleton would flash a grey
    // box over the most important thing on the page.
    if (img.getAttribute('loading') !== 'lazy') return;
    if (img.dataset.alphaSkel) return;
    img.dataset.alphaSkel = '1';

    img.classList.add(SKEL);

    /*
     * Settled before we got here.
     *
     * `complete` alone, NOT `complete && naturalWidth > 0`. Both outcomes are
     * finished business: a decoded image needs no skeleton, and one that has
     * already FAILED will never fire another event, so attaching listeners to
     * it leaves a placeholder shimmering over a hole for the rest of the
     * session. Caught exactly that with an injected broken image - the observer
     * picked it up, the error had already fired, and it never settled.
     *
     * This matters most for the AJAX paths, where markup is inserted and its
     * images can resolve before the MutationObserver callback even runs.
     */
    if (img.complete) {
      settle(img);
      return;
    }

    img.addEventListener('load', function () {
      settle(img);
    });
    // A broken image must not shimmer for the rest of the session.
    img.addEventListener('error', function () {
      settle(img);
    });
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) track(imgs[i]);
  }

  function init() {
    scan(document);

    if (!('MutationObserver' in window)) return;

    /*
     * Everything this theme swaps in over AJAX arrives as replaced innerHTML
     * rather than as appended nodes, so watching the subtree is the only way to
     * catch it: cart drawer contents, cart recommendations, predictive search
     * results, and the product grid after every filter or sort change.
     */
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            track(node);
          } else {
            scan(node);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
